import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="screen-swap"
//
// One sticky phone beside a column of questions (the /aplikacia page): as each
// question reaches the middle of the viewport, the phone shows the app screen
// that answers it. Every step carries its screen as data-screen-src,
// -srcset and -alt; the phone's image is the target that changes.
//
// The swap is a cross-fade: once the new screen is decoded, a copy of the
// image carrying it is laid over the current one and fades in; then the
// current image takes the new source and the copy goes away. The fade is the
// image's own CSS transition (motion-reduce turns it off). The image's parent
// must be positioned. Without JavaScript the phone keeps the hero screen.
export default class extends Controller {
  static targets = ["step", "image"];

  connect() {
    this.current = this.imageTarget.getAttribute("src");
    this.#preload();

    // A band across the middle of the viewport: the step crossing it wins.
    this.observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting);
      if (visible.length) this.#show(visible[visible.length - 1].target);
    }, { rootMargin: "-45% 0px -45% 0px" });

    this.stepTargets.forEach((step) => this.observer.observe(step));
  }

  disconnect() {
    this.observer?.disconnect();
    this.#finish();
  }

  #show(step) {
    const { screenSrc: src, screenSrcset: srcset, screenAlt: alt } = step.dataset;
    if (!src || src === this.current) return;

    this.current = src;
    const image = this.imageTarget;

    const next = new Image();
    if (srcset) next.srcset = srcset;
    next.sizes = image.sizes;
    next.src = src;

    const ready = next.decode ? next.decode().catch(() => {}) : Promise.resolve();
    ready.then(() => {
      if (this.current !== src) return;
      this.#finish();

      const overlay = image.cloneNode();
      overlay.removeAttribute("data-screen-swap-target");
      overlay.removeAttribute("fetchpriority");
      overlay.setAttribute("aria-hidden", "true");
      overlay.alt = "";
      if (srcset) overlay.srcset = srcset;
      overlay.src = src;
      Object.assign(overlay.style, { position: "absolute", inset: "0", opacity: "0" });
      image.after(overlay);
      this.overlay = overlay;

      // Let the overlay paint at 0 first so the opacity change transitions.
      requestAnimationFrame(() => requestAnimationFrame(() => { overlay.style.opacity = "1"; }));

      this.swapTimer = setTimeout(() => {
        if (srcset) image.srcset = srcset;
        image.src = src;
        image.alt = alt || "";
        this.#finish();
      }, this.#fadeMs + 50);
    });
  }

  // Ends a running cross-fade: the overlay goes, the image keeps what it shows.
  #finish() {
    clearTimeout(this.swapTimer);
    this.overlay?.remove();
    this.overlay = null;
  }

  // The screens are small (~30–80 KB); fetching them up front keeps the swap instant.
  #preload() {
    this.stepTargets.forEach(({ dataset }) => {
      if (!dataset.screenSrc) return;
      const image = new Image();
      if (dataset.screenSrcset) image.srcset = dataset.screenSrcset;
      image.sizes = this.imageTarget.sizes;
      image.src = dataset.screenSrc;
    });
  }

  get #fadeMs() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 200;
  }
}
